/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import Tippy from "@tippyjs/react/headless";
import cx from "classnames";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { addDays, format, startOfDay, isSameDay } from "date-fns";
import { enCA, frCA } from "date-fns/locale";
import {
	useDatePicker,
	DatePicker as SunwingDatePicker
} from "@sunwing/shared-components";

import { AppContext } from "../../../context";
import { InputWrapper } from "../../../components/InputWrapper";
import { BRANDS, MODES, DEFAULTS } from "../../../configs";
import { getDefaultDateSettings } from "../utils";
import { useInputSearchContext } from "../../../context/app/searchValuesContext";
import {
	CRUISE_DATE_FIELD_ID,
	FLIGHTS_PASSENGERS_ID,
	FLIGHTS_DATES_FIELD_ID,
	FLIGHTS_DATES_MULTIPLE_FIELD_ID_1,
	FLIGHTS_DATES_MULTIPLE_FIELD_ID_2,
	FLIGHTS_DATES_MULTIPLE_FIELD_ID_3
} from "../../../configs/const";
import {
	isIOS,
	isIpad,
	isAndroid,
	isBrowser,
	isIphonePro,
	isIphoneProMax
} from "../utils/detect-devices";

import "./base.module.scss";
import styles from "./brands.module.scss";

const renderFooterComponent = ({
	id,
	labels,
	classNames,

	isMobile,
	isDefaultLocale,
	isMultipleFlight,
	isResettable,

	ref,

	onClick,
	resetDate,
	onNextView,
	setIsDateFocus
}) => (
	<div
		ref={ref}
		className={cx(classNames.calendarFooter, {
			[styles.calendarFooterFR]:
				!isDefaultLocale &&
				(id === FLIGHTS_DATES_FIELD_ID ||
					id === FLIGHTS_DATES_MULTIPLE_FIELD_ID_1 ||
					id === FLIGHTS_DATES_MULTIPLE_FIELD_ID_2 ||
					id === FLIGHTS_DATES_MULTIPLE_FIELD_ID_3),
			[styles.calendarFooterSingleCalendarFlightIos]:
				id === FLIGHTS_DATES_FIELD_ID && isMobile && isIOS(),
			[styles.calendarFooterSingleCalendarFlightIosProMax]:
				id === FLIGHTS_DATES_FIELD_ID && isMobile && isIphoneProMax(),
			[styles.calendarFooterSingleCalendarFlightIosMultiple]:
				isMobile && isIOS() && isMultipleFlight,
			[styles.calendarFooterSingleCalendarFlightIosMultipleProMax]:
				isMobile && isIphoneProMax() && isMultipleFlight,

			[styles.calendarFooterSingleCalendarFlight]:
				id === FLIGHTS_DATES_FIELD_ID && isMobile && isBrowser() && !isIOS(),
			[styles.calendarFooterSingleCalendarFlightAndroid]:
				id === FLIGHTS_DATES_FIELD_ID && isMobile && isAndroid() && !isIOS(),

			[styles.calendarFooterSingleCalendarFlightMultiple]:
				isMobile && isBrowser() && !isIOS() && isMultipleFlight,
			[styles.calendarFooterSingleCalendarFlightMultipleAndroid]:
				isMobile && isAndroid() && !isIOS() && isMultipleFlight
		})}>
		<div className={cx(classNames.calendarFooterRight)}>
			<button
				onClick={resetDate}
				aria-hidden="true"
				aria-label={labels.resetButton}
				type="button"
				className={classNames.resetText}
				disabled={!isResettable}>
				{labels.resetButton}
			</button>
			<button
				className={classNames.confirmBtn}
				aria-label={isMobile ? labels.next : labels.doneButton}
				type="button"
				onClick={() => {
					if (isMobile) {
						onNextView();
						setIsDateFocus(false);
						onClick();
					} else {
						setIsDateFocus(false);
						onClick();
					}
					window.WEB_CDN_BSB._emitter.emit("widgetToggle", false);
				}}>
				{isMobile ? labels.next : labels.doneButton}
			</button>
		</div>
	</div>
);

const SovBrandDatePicker = ({
	id,
	mode,
	type,
	helperText,
	className,

	numberOfMultipleFlightInputs,

	error,
	disabled,
	isWidgetReset,
	isOnlyDateField,
	openMobileCalendar,
	isMultipleFlight,

	classnames,

	defaultDate,
	availableDates,
	previousDates,

	ParentRefElement,

	onChange,
	setOpenCalendar,
	setMultipleFlightData,
	setNextButtonDisabled,
	setShowPassengerPopover,
	setIsInitialSovMobileInputState,
	handleSovMobileInputSelectionToView
}) => {
	const [useAppState] = AppContext.useAppContext();
	const isStackMobileLayout = useAppState.brand === BRANDS.SOV && !useAppState.sovSimplifiedSearchBar;
	const [
		externalMobileCalendarControl,
		setExternalMobileCalendarControl
	] = useState(false);
	const labels = {
		back: useAppState.getDictionary("back"),
		days: useAppState.getDictionary("days"),
		doneButton: useAppState.getDictionary("done"),
		duration: useAppState.getDictionary("duration"),
		endDate: useAppState.getDictionary("vwq-end-date-input-for-selection"),
		prevButton: useAppState.getDictionary("vwq-previous-month-button"),
		nextButton: useAppState.getDictionary("vwq-next-month-button"),
		resetButton: useAppState.getDictionary("reset"),
		departure: useAppState.getDictionary("departure"),
		return: useAppState.getDictionary("return-vwq"),
		day: useAppState.getDictionary("duration-day"),
		next: useAppState.getDictionary("next")
	};

	const {
		values: { calendarDates },
		storeSearchValues
	} = useInputSearchContext();

	const responsive = {
		default: {
			months: 1,
			vertical: true
		},
		768: {
			months: 2,
			vertical: false
		}
	};

	const inputRef = useRef(null);
	let refElement = useRef(null);
	const dateLocale = useAppState.lang?.toLowerCase() === "fr" ? frCA : enCA;
	const dateFormat =
		useAppState.lang?.toLowerCase() === "fr" ? "iii, dd LLL" : "iii, LLL dd";

	const [isDateFocus, setIsDateFocus] = useState(false);

	const [minDate, setMinDate] = useState(startOfDay(new Date()));
	const [maxDate, setMaxDate] = useState(addDays(startOfDay(new Date()), 365));

	const {
		showCalendar,
		setShowCalendar,
		handleDate,
		date,
		setDate,
		isResettable,
		resetDate,
		...dateRangePickerProps
	} = useDatePicker({
		date: defaultDate?.toDate() ?? addDays(new Date(), 1),
		handleDateChange: onChange
	});

	useEffect(() => {
		if ((!availableDates && !previousDates && !isWidgetReset && useAppState.sovSimplifiedSearchBar) || defaultDate) {
			return;
		}

		const dates = getDefaultDateSettings(availableDates, previousDates);

		setMinDate(dates.minDate.toDate());
		setMaxDate(dates.maxDate.toDate());
		onChange(dates.defaultDates);

		if (useAppState.sovSimplifiedSearchBar && calendarDates?.length > 0) {
			if (
				!isSameDay(dayjs(calendarDates)?.toDate(), dates?.defaultDates?.[0])
			) {
				setDate(dayjs(dates?.defaultDates[0]).toDate());
			}
		} else if (
			!isSameDay(dayjs(defaultDate)?.toDate(), dates?.defaultDates?.[0])
		) {
			setDate(dayjs(dates?.defaultDates?.[0]).toDate());
		} else {
			setDate(dayjs(dates.defaultDates[0]).toDate());
		}

		handleDate();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [availableDates, previousDates, isWidgetReset]);

	useEffect(() => {
		if (
			ParentRefElement?.current &&
			openMobileCalendar &&
			useAppState.sovSimplifiedSearchBar
		) {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			refElement = ParentRefElement;
			setExternalMobileCalendarControl(true);
		}
	}, []);

	useEffect(() => {
		const handleDetectOutsideClick = e => {
			let maxWidth = "(max-width: 768px)";
			if (isStackMobileLayout) {
				maxWidth =  "(max-width: 767px)";
			}
			
			if (window.matchMedia(maxWidth).matches) {
				return;
			}

			if (showCalendar) {
				if (
					typeof e.composedPath === "function" &&
					!e.composedPath().includes(inputRef.current)
				) {
					e.stopPropagation();
					e.stopImmediatePropagation();
					e.preventDefault();

					handleDate();
					setShowCalendar(false);
					setIsDateFocus(false);
				}
			}
		};

		if (useAppState.reactDOM !== document) {
			document.addEventListener("click", handleDetectOutsideClick);
		}

		return () => {
			if (useAppState.reactDOM !== document) {
				document.removeEventListener("click", handleDetectOutsideClick);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [useAppState.reactDOM, inputRef, showCalendar, setShowCalendar]);

	useEffect(() => {
		if (
			showCalendar &&
			mode !== MODES.TABS &&
			useAppState.brand.toLowerCase() !== BRANDS.SOV.toLowerCase()
		) {
			disableBodyScroll(refElement.current, {
				reserveScrollBarGap: true,
				allowTouchMove: el => {
					let _el = el;
					while (_el && _el.parentElement) {
						if (_el.id === "booking-search-box-wrapper") {
							return true;
						}

						_el = _el.parentElement;
					}
					return false;
				}
			});
		} else {
			enableBodyScroll(refElement.current);
		}
	}, [showCalendar, refElement, mode, useAppState.brand]);

	let _label = useAppState.getDictionary("departing-by-date");

	if (id === FLIGHTS_DATES_FIELD_ID) {
		if (type !== "range") {
			_label = useAppState.getDictionary("departure");
		} else {
			_label = useAppState.getDictionary("date");
		}
	}

	return (
		<InputWrapper
			key={useAppState.lang}
			brand={useAppState.brand}
			ref={inputRef}
			label={
				useAppState.sovSimplifiedSearchBar
					? useAppState.getDictionary("when")
					: _label
			}
			id={id}
			error={error}
			helperText={helperText}
			className={className}
			focusControl={useAppState.brand === BRANDS.SWG && isDateFocus}
			hasSelectedItem={!!date}
			isCalendarOpen={showCalendar}
			isOnlyDateInput={isOnlyDateField && !showCalendar}>
			{() => (
				<div>
					<fieldset
						ref={refElement}
						className={cx(styles.dateInputContainer, {
							[styles[useAppState.brand]]: styles[useAppState.brand],
							[styles.dateInputContainerStackLayout]: isStackMobileLayout,
							[styles.dateInputContainerMultipleFlight]: isMultipleFlight,
							[styles.dateInputContainerSingleCalendar]: type !== "range",
							[styles.dateInputContainerSingleCalendarPro]:
								type !== "range" && isIphonePro(),
							[styles.dateInputContainerTabsFlight]: mode === MODES.TABS,
							[styles.dateInputContainerANDROIDTABSINGLE]:
								isAndroid() && window.matchMedia("(width: 712px)").matches,
							[styles.dateInputContainerIPADMINISINGLE]:
								isIpad() &&
								!isAndroid() &&
								window.matchMedia("(width: 768px)").matches // iPad mini width: 768px
						})}>
						<label
							aria-hidden="true"
							htmlFor="departure"
							className={cx(styles.selectedDate, {
								[styles.selectedDateDisabled]: disabled,
								[styles.selectedDateStackLayout]: isStackMobileLayout,
								[styles.selectedDateStack]: isStackMobileLayout,
								[styles.selectedDateIOSSingleDate]: isIpad()
							})}
							onClick={() => {
								if (!disabled) {
									setShowCalendar(true);
									setIsDateFocus(true);

									setIsInitialSovMobileInputState(false);

									handleSovMobileInputSelectionToView(
										id,
										useAppState.getDictionary("when-are-you-going")
									);
									window.WEB_CDN_BSB._emitter.emit("widgetToggle", true);
								}
							}}>
							<span className={cx(styles.srOnly)}>{labels.departure}</span>
							<input
								readOnly
								type="text"
								value={format(date, dateFormat, {
									locale: dateLocale
								})}
							/>
						</label>
						{!useAppState.sovSimplifiedSearchBar && (
							<span
								className={styles.calendar}
								role="button"
								aria-hidden={true}
								onClick={() => {
									setShowCalendar(!showCalendar);
									window.WEB_CDN_BSB._emitter.emit("widgetToggle", !showCalendar);
								}}
							/>
						)}
					</fieldset>
					<Tippy
						key={externalMobileCalendarControl || showCalendar}
						reference={refElement?.current}
						visible={externalMobileCalendarControl || showCalendar}
						interactive={true}
						appendTo={refElement?.current?.parentElement}
						placement="bottom"
						role="tooltip"
						arrow={true}
						render={attrs => (
							<div
								className={cx(styles.calendarPopupWrapper, {
									[styles[useAppState.brand]]: styles[useAppState.brand],
									[styles.calendarPopupWrapperOffset]:
										id === CRUISE_DATE_FIELD_ID
								})}>
								<span
									className={cx({
										[classnames?.listboxTail]: showCalendar,
										[classnames?.listboxTailMultipleFLight]: isMultipleFlight
									})}
								/>
								<SunwingDatePicker
									{...attrs}
									classNames={styles}
									locale={dateLocale}
									responsive={responsive}
									minDate={minDate}
									maxDate={maxDate}
									date={date}
									setDate={setDate}
									monthDisplayFormat="MMMM yyyy"
									dateFormat={dateFormat}
									selectedDayBackgroundColor={["#bef564"]}
									reactCalendarProps={{
										ariaLabels: {
											dateInput: {
												startDate: useAppState.getDictionary("start-date"),
												endDate: useAppState.getDictionary("end-date")
											},
											monthPicker: useAppState.getDictionary("month-picker"),
											yearPicker: useAppState.getDictionary("year-picker"),
											prevButton: useAppState.getDictionary("prev-button"),
											nextButton: useAppState.getDictionary("next-button")
										}
									}}
									renderBackIcon={
										useAppState.sovSimplifiedSearchBar
											? () => <></>
											: ({ onClick }) => (
													// eslint-disable-next-line react/jsx-indent
													<button
														type="button"
														aria-hidden="true"
														aria-label={labels.back}
														className={styles.calendarBackIcon}>
														<FontAwesomeIcon
															onClick={() => {
																onClick();
																setIsDateFocus(false);
																window.WEB_CDN_BSB._emitter.emit("widgetToggle", false);
															}}
															icon={faChevronLeft}
														/>
													</button>
											  )
									}
									renderFooterComponent={
										footerProps =>
											renderFooterComponent({
												...footerProps,
												id,
												labels,
												isResettable,
												resetDate,
												setIsDateFocus,
												classNames: {
													calendarFooter: `${styles.calendarFooter} ${styles.calendarFooterRightFLoat}`,
													calendarFooterLeft: styles.calendarFooterLeft,
													calendarFooterRight: styles.calendarFooterRight,
													srOnly: styles.srOnly,
													resetText: styles.resetText,
													confirmBtn: styles.confirmBtn
												},
												isDefaultLocale: useAppState.lang === DEFAULTS.LANGUAGE,
												isMobile: useAppState.sovSimplifiedSearchBar,
												isMultipleFlight,
												onNextView: () => {
													setShowCalendar(false);
													setExternalMobileCalendarControl(false);
													setOpenCalendar(false);

													if (
														(type === "date" && !isMultipleFlight) ||
														(id === FLIGHTS_DATES_MULTIPLE_FIELD_ID_3 &&
															isMultipleFlight) ||
														(numberOfMultipleFlightInputs === 1 &&
															id === FLIGHTS_DATES_MULTIPLE_FIELD_ID_2 &&
															isMultipleFlight)
													) {
														handleSovMobileInputSelectionToView(
															FLIGHTS_PASSENGERS_ID,
															useAppState.getDictionary("who-is-coming")
														);
														setShowPassengerPopover(true);
														setIsInitialSovMobileInputState(false);
													} else {
														setNextButtonDisabled(true);
														setShowPassengerPopover(false);
														setIsInitialSovMobileInputState(true);
														handleSovMobileInputSelectionToView(
															"multicity-default",
															""
														);
													}

													if (!isMultipleFlight) {
														storeSearchValues({
															type: "calendarDates",
															calendarDates: [dayjs(date)]
														});
													}
												}
											})
										// eslint-disable-next-line react/jsx-curly-newline
									}
									onChange={val => {
										const updateSelectedDate = (fieldId, dateValue) => {
											setMultipleFlightData(prev => ({
												...prev,
												[fieldId]: {
													...prev[fieldId],
													selectedDate: dayjs(dateValue)
												}
											}));
											setShowCalendar(false);
										};

										if (id === FLIGHTS_DATES_MULTIPLE_FIELD_ID_1) {
											updateSelectedDate("flight1", val);
										} else if (id === FLIGHTS_DATES_MULTIPLE_FIELD_ID_2) {
											updateSelectedDate("flight2", val);
										} else if (id === FLIGHTS_DATES_MULTIPLE_FIELD_ID_3) {
											updateSelectedDate("flight3", val);
										} else {
											handleDate(val);
										}
									}}
									{...dateRangePickerProps}
								/>
							</div>
						)}
					/>
				</div>
			)}
		</InputWrapper>
	);
};

SovBrandDatePicker.propTypes = {
	id: PropTypes.string.isRequired,
	mode: PropTypes.string,
	helperText: PropTypes.string,
	nextLeg: PropTypes.number,
	numberOfMultipleFlightInputs: PropTypes.number,
	type: PropTypes.string,
	className: PropTypes.string,

	error: PropTypes.bool,
	disabled: PropTypes.bool,
	isWidgetReset: PropTypes.bool,
	isOnlyDateField: PropTypes.bool,
	openMobileCalendar: PropTypes.bool,
	isMultipleFlight: PropTypes.bool,

	defaultDate: PropTypes.instanceOf(dayjs),
	classnames: PropTypes.shape({
		listboxTail: PropTypes.string,
		listboxTailMultipleFLight: PropTypes.string
	}),
	availableDates: PropTypes.shape({
		RolloverAvailableDays: PropTypes.number,
		RolloverMaxAvailableDays: PropTypes.number,
		DefaultAvailableDate: PropTypes.string,
		DefaultMaxAvailableDate: PropTypes.string
	}),
	previousDates: PropTypes.arrayOf(PropTypes.string),
	minDate: PropTypes.instanceOf(Date),
	maxDate: PropTypes.instanceOf(Date),

	ParentRefElement: PropTypes.shape({ current: PropTypes.node }),

	onChange: PropTypes.func.isRequired,
	setOpenCalendar: PropTypes.func,
	setNextButtonDisabled: PropTypes.func,
	setMultipleFlightData: PropTypes.func,
	setShowPassengerPopover: PropTypes.func,
	setnumberOfMultipleFlightInputs: PropTypes.func,
	setIsInitialSovMobileInputState: PropTypes.func,
	handleSovMobileInputSelectionToView: PropTypes.func
};

SovBrandDatePicker.defaultProps = {
	mode: MODES.SEARCH,
	nextLeg: undefined,
	helperText: undefined,
	numberOfMultipleFlightInputs: undefined,
	type: undefined,
	className: undefined,
	classnames: undefined,

	error: false,
	disabled: false,
	isWidgetReset: false,
	isOnlyDateField: false,
	isMultipleFlight: false,
	openMobileCalendar: false,

	availableDates: undefined,
	previousDates: undefined,

	defaultDate: undefined,
	minDate: addDays(new Date(), 1),
	maxDate: addDays(new Date(), 365),

	ParentRefElement: null,

	setOpenCalendar: () => {},
	setNextButtonDisabled: () => {},
	setMultipleFlightData: () => {},
	setShowPassengerPopover: () => {},
	setnumberOfMultipleFlightInputs: () => {},
	setIsInitialSovMobileInputState: () => {},
	handleSovMobileInputSelectionToView: () => {}
};

export default SovBrandDatePicker;
export { SovBrandDatePicker };
